import jwtDecode from "jwt-decode";
import axios from "axios";

import config from "../../config";
import TokenDebugger from "../TokenDebugger";
import TokenNotifications from "../TokenNotifications";

// content type
axios.defaults.headers.post["Content-Type"] = "application/json";
axios.defaults.baseURL = config.API_URL;
axios.defaults.timeout = 10000; // 10 second timeout

// Flag to prevent multiple concurrent refresh attempts
let isRefreshing = false;
let failedQueue = [];

// Token refresh retry configuration
const MAX_REFRESH_RETRIES = 3;
let refreshRetryCount = 0;

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  
  failedQueue = [];
};

// Function to handle logout and redirect
const handleLogout = (reason = 'Token expired') => {
  console.log(`🚪 Logging out user - Reason: ${reason}`);
  TokenNotifications.showLogoutNotice(reason);
  
  // Clear all tokens and user data
  localStorage.removeItem(config.AUTH_SESSION_KEY);
  localStorage.removeItem(config.REFRESH_TOKEN_KEY);
  localStorage.removeItem('user_profile');
  setAuthorization(null);
  
  // Reset refresh state
  isRefreshing = false;
  refreshRetryCount = 0;
  failedQueue = [];
  
  // Only redirect if we're not already on the login page
  if (!window.location.pathname.includes('/auth/login')) {
    console.log('🔄 Redirecting to login page...');
    TokenNotifications.notify('info', 'Redirecting to login page...');
    // Add a small delay to ensure localStorage is cleared
    setTimeout(() => {
      window.location.href = '/auth/login';
    }, 100);
  }
};

// Function to check if we should attempt token refresh
const shouldAttemptRefresh = (error, originalRequest) => {
  return (
    error.response?.status === 401 &&
    !originalRequest._retry &&
    originalRequest.url !== '/auth/refresh' &&
    refreshRetryCount < MAX_REFRESH_RETRIES
  );
};

// Add request interceptor for debugging
axios.interceptors.request.use(
  (config) => {
    console.log('Axios Request:', {
      url: config.url,
      method: config.method,
      baseURL: config.baseURL,
      data: config.data,
      headers: config.headers
    });
    return config;
  },
  (error) => {
    console.error('Request Error:', error);
    return Promise.reject(error);
  }
);

// intercepting to capture errors
axios.interceptors.response.use(
  (response) => {
    console.log('Axios Response Success:', {
      status: response.status,
      data: response.data,
      url: response.config.url
    });
    return response;
  },
  async (error) => {
    console.error('Axios Response Error:', {
      message: error.message,
      code: error.code,
      response: error.response,
      request: error.request,
      config: error.config
    });
      const originalRequest = error.config;
    
    // Check if error is 401 and this is not a refresh request
    if (shouldAttemptRefresh(error, originalRequest)) {
      if (isRefreshing) {
        // If already refreshing, queue this request
        console.log('🔄 Queueing request during token refresh...');
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(token => {
          originalRequest.headers['Authorization'] = 'Bearer ' + token;
          return axios(originalRequest);
        }).catch(err => {
          return Promise.reject(err);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;
      refreshRetryCount++;
      
      const refreshToken = localStorage.getItem(config.REFRESH_TOKEN_KEY);
      
      if (refreshToken) {        try {
          console.log(`🔄 Attempting to refresh token (attempt ${refreshRetryCount}/${MAX_REFRESH_RETRIES})...`);
          TokenNotifications.showRefreshWarning();
          
          // Check if refresh token is still valid before attempting refresh
          try {
            const decoded = jwtDecode(refreshToken);
            const currentTime = Date.now() / 1000;
            
            if (decoded.exp <= currentTime) {
              throw new Error('Refresh token expired');
            }
          } catch (decodeError) {
            throw new Error('Invalid refresh token');
          }
          
          const response = await axios.post('/auth/refresh', {
            refresh_token: refreshToken
          });
          
          if (response.data && response.data.access_token) {
            console.log('✅ Token refresh successful');
            TokenNotifications.showRefreshSuccess();
            refreshRetryCount = 0; // Reset retry count on success
            
            localStorage.setItem(config.AUTH_SESSION_KEY, response.data.access_token);
            if (response.data.refresh_token) {
              localStorage.setItem(config.REFRESH_TOKEN_KEY, response.data.refresh_token);
            }
            setAuthorization(response.data.access_token);
            
            // Process the queued requests
            processQueue(null, response.data.access_token);
            
            // Retry the original request with new token
            originalRequest.headers['Authorization'] = 'Bearer ' + response.data.access_token;
            return axios(originalRequest);
          } else {
            throw new Error('Invalid refresh response');
          }        } catch (refreshError) {
          console.error('❌ Token refresh failed:', refreshError.message);
          TokenNotifications.showRefreshError(refreshError.message);
          
          // Process the queued requests with error
          processQueue(refreshError, null);
          
          // Handle logout and redirect
          handleLogout(`Token refresh failed: ${refreshError.message}`);
          
          return Promise.reject(new Error('Authentication failed. Please login again.'));
        } finally {
          isRefreshing = false;
        }
      } else {
        console.warn('⚠️ No refresh token available');
        isRefreshing = false;
        
        // Handle logout and redirect
        handleLogout('No refresh token available');
        
        return Promise.reject(new Error('No refresh token available. Please login again.'));
      }
    }    // Handle refresh token endpoint failures
    if (error.response?.status === 401 && originalRequest.url === '/auth/refresh') {
      console.error('🚫 Refresh token is invalid or expired');
      console.log('🔍 Token status before clearing:');
      TokenDebugger.logTokenInfo();
      
      // Handle logout and redirect
      handleLogout('Refresh token invalid or expired');
      
      return Promise.reject(new Error('Refresh token expired. Please login again.'));
    }
    
    // Handle case where max refresh retries exceeded
    if (error.response?.status === 401 && refreshRetryCount >= MAX_REFRESH_RETRIES) {
      console.error(`🚫 Max refresh retries (${MAX_REFRESH_RETRIES}) exceeded`);
      handleLogout('Max refresh retries exceeded');
      return Promise.reject(new Error('Authentication failed after multiple attempts. Please login again.'));
    }
    
    // Any status codes that falls outside the range of 2xx cause this function to trigger
    let message;

    if (error && error.response && error.response.status === 404) {
      // window.location.href = '/not-found';
    } else if (error && error.response && error.response.status === 403) {
      window.location.href = "/access-denied";
    } else {      // Check if it's a network error
      if (!error.response) {
        console.error("Network Error Details:", error);
        TokenNotifications.showNetworkError();
        message = error.code === 'ECONNABORTED' ? 'Request timeout' : 'Network error - please check your connection';
        return Promise.reject(new Error(message));
      }
      
      switch (error.response.status) {
        case 401:
          message = "Invalid credentials";
          // Clear tokens on 401 (if refresh already failed)
          if (originalRequest._retry) {
            localStorage.removeItem(config.AUTH_SESSION_KEY);
            localStorage.removeItem(config.REFRESH_TOKEN_KEY);
            window.location.href = '/auth/login';
          }
          break;
        case 403:
          message = "Access Forbidden";
          break;
        case 404:
          message = "Sorry! the data you are looking for could not be found";
          break;
        default: {
          message =
            error.response && error.response.data
              ? error.response.data["message"]
              : error.message || error;
        }
      }
      return Promise.reject(new Error(message));
    }
  }
);

/**
 * Sets the default authorization
 * @param {*} token
 */
const setAuthorization = (token) => {
  if (token) {
    axios.defaults.headers.common["Authorization"] = "Bearer " + token;
  } else {
    delete axios.defaults.headers.common["Authorization"];
  }
};

const getUserFromStorage = () => {
  const user = localStorage.getItem(config.AUTH_SESSION_KEY);
  return user ? (typeof user == "object" ? user : JSON.parse(user)) : null;
};

const getTokenFromStorage = () => {
  return localStorage.getItem(config.AUTH_SESSION_KEY);
};
class APICore {  /**
   * Fetches data from given url
   */
  get = (url, params) => {
    let response;
    if (params) {
      const queryString = params
        ? Object.keys(params)
          .map((key) => key + "=" + params[key])
          .join("&")
        : "";
      response = axios.get(`${url}?${queryString}`, params);
    } else {
      response = axios.get(`${url}`, params);
    }
    return response;
  };

  getFile = (url, params) => {
    let response;
    if (params) {
      const queryString = params
        ? Object.keys(params)
          .map((key) => key + "=" + params[key])
          .join("&")
        : "";
      response = axios.get(`${url}?${queryString}`, { responseType: "blob" });
    } else {
      response = axios.get(`${url}`, { responseType: "blob" });
    }
    return response;
  };

  getMultiple = (urls, params) => {
    const reqs = [];
    let queryString = "";
    if (params) {
      queryString = params
        ? Object.keys(params)
          .map((key) => key + "=" + params[key])
          .join("&")
        : "";
    }

    for (const url of urls) {
      reqs.push(axios.get(`${url}?${queryString}`));
    }
    return axios.all(reqs);
  };  /**
   * post given data to url
   */
  create = (url, data) => {
    console.log("APICore.create called with:", { url, data, baseURL: axios.defaults.baseURL });
    const promise = axios.post(url, data);
    console.log("APICore.create axios.post promise:", promise);
    return promise;
  };
  /**
   * Updates patch data
   */
  updatePatch = (url, data) => {
    return axios.patch(url, data);
  };
  /**
   * Updates data
   */
  update = (url, data) => {
    return axios.put(url, data);
  };
  /**
   * Deletes data
   */
  delete = (url) => {
    return axios.delete(url);
  };
  /**
   * post given data to url with file
   */
  createWithFile = (url, data) => {
    const formData = new FormData();
    for (const k in data) {
      formData.append(k, data[k]);
    }

    const config = {
      headers: {
        ...axios.defaults.headers,
        "content-type": "multipart/form-data",
      },
    };
    return axios.post(url, formData, config);
  };
  /**
   * post given data to url with file
   */
  updateWithFile = (url, data) => {
    const formData = new FormData();
    for (const k in data) {
      formData.append(k, data[k]);
    }

    const config = {
      headers: {
        ...axios.defaults.headers,
        "content-type": "multipart/form-data",
      },
    };
    return axios.patch(url, formData, config);
  };  refreshToken = async () => {
    const refreshToken = this.getRefreshToken();
    
    if (!refreshToken) {
      console.warn("No refresh token available");
      return false;
    }

    // Check if we're already refreshing to prevent multiple calls
    if (isRefreshing) {
      console.log("Token refresh already in progress");
      return false;
    }

    try {
      isRefreshing = true;
      console.log("Starting token refresh...");
      
      const response = await axios.post('/auth/refresh', {
        refresh_token: refreshToken
      });

      if (response.data && response.data.access_token) {
        // Update tokens in storage
        localStorage.setItem(config.AUTH_SESSION_KEY, response.data.access_token);
        if (response.data.refresh_token) {
          localStorage.setItem(config.REFRESH_TOKEN_KEY, response.data.refresh_token);
        }
        setAuthorization(response.data.access_token);
        console.log("Token refreshed successfully");
        return true;
      } else {
        throw new Error('Invalid refresh response');
      }
    } catch (error) {
      console.error("Token refresh failed:", error);
      // Clear all tokens on refresh failure
      this.setLoggedInUser(null);
      return false;
    } finally {
      isRefreshing = false;
    }
  };

  isUserAuthenticated = async () => {
    const token = getTokenFromStorage();

    if (!token) {
      return false;
    }
    
    try {
      const decoded = jwtDecode(token);
      const currentTime = Date.now() / 1000;
      
      if (decoded.exp < currentTime) {
        console.warn("access token expired, attempting to refresh...");
        
        // Try to refresh the token
        const refreshSuccess = await this.refreshToken();
        if (refreshSuccess) {
          return true;
        } else {
          return false;
        }
      } else {
        return true;
      }
    } catch (error) {
      console.error("Error decoding token:", error);
      return false;
    }
  };
  setLoggedInUser = (userData) => {
    if (userData) {
      // Reset refresh retry count on successful login
      refreshRetryCount = 0;
      
      // For Go Fiber API response format
      if (userData.access_token) {
        localStorage.setItem(config.AUTH_SESSION_KEY, userData.access_token);
        localStorage.setItem(config.REFRESH_TOKEN_KEY, userData.refresh_token);
        setAuthorization(userData.access_token);
      } else if (userData.token) {
        // Legacy format support
        localStorage.setItem(config.AUTH_SESSION_KEY, userData.token);
        setAuthorization(userData.token);
      }
      
      // Store user profile if available
      if (userData.user) {
        localStorage.setItem('user_profile', JSON.stringify(userData.user));
      }
      
      console.log('✅ User logged in successfully');
    } else {
      handleLogout('User logged out');
    }
  };

  /**
   * Returns the logged in user token
   */
  getLoggedInUser = () => {
    const token = getTokenFromStorage();
    const userProfile = localStorage.getItem('user_profile');
    
    if (token) {
      return {
        token: token,
        user: userProfile ? JSON.parse(userProfile) : null
      };
    }
    return null;
  };

  getAccessToken = () => {
    return getTokenFromStorage();
  };

  getRefreshToken = () => {
    return localStorage.getItem(config.REFRESH_TOKEN_KEY);
  };
  setUserInSession = (modifiedUser) => {
    const userProfile = localStorage.getItem('user_profile');
    if (userProfile) {
      const user = JSON.parse(userProfile);
      localStorage.setItem('user_profile', JSON.stringify({ ...user, ...modifiedUser }));
    }
  };

  /**
   * Force refresh the access token
   */
  forceRefreshToken = async () => {
    return await this.refreshToken();
  };
  /**
   * Check if refresh token exists and is valid
   */
  hasValidRefreshToken = () => {
    const refreshToken = this.getRefreshToken();
    if (!refreshToken) return false;

    try {
      const decoded = jwtDecode(refreshToken);
      const currentTime = Date.now() / 1000;
      return decoded.exp > currentTime;
    } catch (error) {
      console.error("Error decoding refresh token:", error);
      return false;
    }
  };

  /**
   * Clear refresh state (useful for testing or manual intervention)
   */
  clearRefreshState = () => {
    isRefreshing = false;
    failedQueue.length = 0;
  };

  /**
   * Get current refresh state
   */
  getRefreshState = () => {
    return {
      isRefreshing,
      queueLength: failedQueue.length
    };
  };
}

/*
Check if token available in storage
*/
const token = getTokenFromStorage();
if (token) {
  setAuthorization(token);
}

export { APICore, setAuthorization };
